
import React, { useState } from 'react';
import { X, FolderOpen, Layers, Loader2, Check, Map as MapIcon, FolderPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CampingSpot, GpxRoute, UserContentFolder, CollaborativeMap, WaypointIcon } from '../../types';
import { addWaypointContent, createFolder } from '../../services/myContentService';
import { BRAND } from '../../constants/mapColors';
import { addSpotToMap, addRouteToMap } from '../../services/collaborativeMapsService';

interface SaveToMyContentModalProps {
  spot?: CampingSpot;
  route?: GpxRoute;
  userId: string;
  isPaidUser: boolean;
  folders: UserContentFolder[];
  collaborativeMaps?: CollaborativeMap[];
  onClose: () => void;
  onSaved: () => void;
  onShowNudge?: (key: 'content_folders') => void;
}

const SaveToMyContentModal: React.FC<SaveToMyContentModalProps> = ({
  spot, route, userId, isPaidUser, folders, collaborativeMaps,
  onClose, onSaved, onShowNudge,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const doSave = async (folderId: string | null) => {
    if (spot) {
      await addWaypointContent(
        userId,
        spot.location.latitude,
        spot.location.longitude,
        spot.name,
        'camp' as WaypointIcon,
        BRAND,
        spot.description?.slice(0, 200),
        folderId,
        spot.photoUrls?.[0] || null
      );
    } else if (route && route.center) {
      await addWaypointContent(
        userId,
        route.center.latitude,
        route.center.longitude,
        route.name,
        'star' as WaypointIcon,
        '#3b82f6',
        route.description?.slice(0, 200),
        folderId,
        route.imageUrls?.[0] || null
      );
    }
  };

  const handleSaveToMyContent = async (folderId: string | null) => {
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      await doSave(folderId);
      onSaved();
    } catch {
      setError(t('myContent.save_failed', 'Kunne ikke lagre. Prøv igjen.'));
      setSaving(false);
    }
  };

  // Opprett ny mappe og lagre rett inn i den.
  const handleCreateFolderAndSave = async () => {
    const name = newFolderName.trim();
    if (!name || saving) return;
    setError(null);
    setSaving(true);
    try {
      const newFolderId = await createFolder(userId, name);
      await doSave(newFolderId);
      onSaved();
    } catch {
      setError(t('myContent.save_failed', 'Kunne ikke lagre. Prøv igjen.'));
      setSaving(false);
    }
  };

  const handleAddToCollabMap = async (mapId: string) => {
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      if (spot) await addSpotToMap(mapId, spot.id);
      else if (route) await addRouteToMap(mapId, route.id);
      onSaved();
    } catch {
      setError(t('myContent.add_to_list_failed', 'Kunne ikke legge til i lista. Prøv igjen.'));
      setSaving(false);
    }
  };

  const visibleCollabMaps = collaborativeMaps?.filter(m => m.spotIds || m.id) ?? [];

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-sm bg-[var(--bg-surface)] rounded-t-2xl sm:rounded-2xl border border-[var(--border-secondary)] shadow-xl h-[60dvh] sm:h-[50vh] flex flex-col overflow-hidden mx-0 sm:mx-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
          <h3 className="text-[12px] font-bold text-[var(--text-primary)]">{t('myContent.save_title')}</h3>
          <button onClick={onClose} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            <X size={14} />
          </button>
        </div>

        {error && (
          <div role="alert" className="mx-4 mb-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-tiny flex-shrink-0">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1">
          {saving ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-emerald-400" />
            </div>
          ) : (
            <>
              {/* My Content folders */}
              <button
                onClick={() => handleSaveToMyContent(null)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] transition-colors"
              >
                <Layers size={14} /> {t('myContent.root_level')}
              </button>

              {isPaidUser ? (
                folders.map(f => (
                  <button
                    key={f.id}
                    onClick={() => handleSaveToMyContent(f.id)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] transition-colors"
                  >
                    <FolderOpen size={14} style={{ color: f.color }} /> {f.name}
                  </button>
                ))
              ) : folders.length > 0 ? null : null}

              {/* Ny mappe — opprett rett her i mobilvisning */}
              {isPaidUser ? (
                creatingFolder ? (
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <FolderPlus size={14} className="text-emerald-400 shrink-0" />
                    <input
                      autoFocus
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateFolderAndSave();
                        if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
                      }}
                      placeholder={t('myContent.folder_name_placeholder')}
                      className="flex-1 min-w-0 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-lg px-2.5 py-1.5 text-tiny text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                    />
                    <button
                      onClick={handleCreateFolderAndSave}
                      disabled={!newFolderName.trim()}
                      className="p-2 rounded-lg bg-emerald-600 text-white disabled:opacity-40 active:scale-95 transition-all shrink-0"
                      aria-label={t('myContent.new_folder')}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}
                      className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0"
                      aria-label={t('common.cancel', 'Avbryt')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setCreatingFolder(true)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny font-bold text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                  >
                    <FolderPlus size={14} /> {t('myContent.new_folder')}
                  </button>
                )
              ) : (
                <button
                  onClick={() => onShowNudge?.('content_folders')}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny font-bold text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                >
                  <FolderPlus size={14} /> {t('myContent.new_folder')}
                </button>
              )}

              {/* Collaborative maps section */}
              {visibleCollabMaps.length > 0 && (
                <>
                  <div className="border-t border-[var(--border-primary)] my-2" />
                  <p className="text-3xs font-bold text-[var(--text-muted)] uppercase tracking-wider px-1 py-1">
                    {t('myContent.collab_maps_section')}
                  </p>
                  {visibleCollabMaps.map(m => (
                    <button
                      key={m.id}
                      onClick={() => handleAddToCollabMap(m.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] transition-colors"
                    >
                      <MapIcon size={14} className="text-sky-400" />
                      <span className="flex-1 text-left truncate">{m.name}</span>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveToMyContentModal;
